import { downloadBlob } from './helpers';

function rtfEscape(s) {
  if (s == null) return '';
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}')
    .replace(/[\u0080-\uFFFF]/g, (c) => `\\u${c.charCodeAt(0)}?`)
    .replace(/\t/g, '\\tab ')
    .replace(/\n/g, '\\line ');
}

function hexToRtfColor(hex) {
  const h = (hex || '#000000').replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function exportToWord(data, template, accent, accent2, name) {
  const filename = (name || 'resume').replace(/[^a-z0-9]+/gi, '_').toLowerCase() + '.rtf';

  const accentRGB = hexToRtfColor(accent);
  const accent2RGB = accent2 ? hexToRtfColor(accent2) : null;

  let colorTable = `{\\colortbl;\\red17\\green17\\blue17;\\red60\\green60\\blue60;\\red232\\green232\\blue232;\\red${accentRGB.r}\\green${accentRGB.g}\\blue${accentRGB.b};`;
  if (accent2RGB) {
    colorTable += `\\red${accent2RGB.r}\\green${accent2RGB.g}\\blue${accent2RGB.b};`;
  }
  colorTable += '}';

  const useSerif = template.bodyFont.includes('Garamond') || template.bodyFont.includes('Cambria') || template.bodyFont.includes('Georgia');
  const fontTable = useSerif
    ? `{\\fonttbl{\\f0\\froman\\fcharset0 Garamond;}{\\f1\\fswiss\\fcharset0 Arial;}}`
    : `{\\fonttbl{\\f0\\fswiss\\fcharset0 Arial;}{\\f1\\froman\\fcharset0 Garamond;}}`;

  const out = [];

  out.push(`{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat`);
  out.push(fontTable);
  out.push(colorTable);
  out.push(`\\paperw12240\\paperh15840\\margl1080\\margr1080\\margt1080\\margb1080`);
  out.push(`\\f0\\fs22\\cf1`);

  if (data.name) {
    const align = template.nameAlign === 'left' || template.nameAlign === 'asymmetric' ? '\\ql' : '\\qc';
    const nameUpper = template.nameStyle?.includes('uppercase') || template.nameStyle?.includes('tracked');
    const nameSize = nameUpper ? 48 : 56;
    const nameText = nameUpper ? data.name.toUpperCase() : data.name;
    out.push(`{\\pard${align}\\sb0\\sa120\\fs${nameSize}\\b\\cf1 ${rtfEscape(nameText)}\\par}`);
  }

  const contactItems = [
    data.contact.location,
    data.contact.phone,
    data.contact.email,
    data.contact.linkedin,
    data.contact.website,
  ].filter(Boolean);
  if (contactItems.length) {
    const align = template.nameAlign === 'left' || template.nameAlign === 'asymmetric' ? '\\ql' : '\\qc';
    out.push(`{\\pard${align}\\sb0\\sa240\\fs19\\cf2 ${rtfEscape(contactItems.join('  |  '))}\\par}`);
  }

  const sectionHeader = (text) => {
    const isBanner = ['banner', 'banner-soft', 'banner-underline'].includes(template.sectionStyle);
    if (isBanner) {
      out.push(`{\\pard\\qc\\sb240\\sa120\\cbpat3\\cf4\\fs20\\b ${rtfEscape(text.toUpperCase())}\\b0\\par}`);
    } else {
      out.push(`{\\pard\\ql\\sb240\\sa60\\cf4\\fs19\\b ${rtfEscape(text.toUpperCase())}\\b0\\par}`);
      out.push(`{\\pard\\ql\\sb0\\sa120\\brdrb\\brdrs\\brdrw10\\brdrcf4\\par}`);
    }
  };

  const s = data.summary || {};
  const hasSummary = s.tagline || (s.bullets && s.bullets.length) || (s.skills && s.skills.length);
  if (hasSummary) {
    sectionHeader('Summary');
    if (s.bullets && s.bullets.length) {
      s.bullets.forEach((b) => {
        out.push(`{\\pard\\fi-280\\li360\\sa60\\cf1\\fs22 \\u8226? \\tab ${rtfEscape(b)}\\par}`);
      });
    }
    if (s.tagline) {
      out.push(`{\\pard\\ql\\sa60\\cf1\\fs22\\i ${rtfEscape(s.tagline)}\\i0\\par}`);
    }
    if (s.skills && s.skills.length) {
      out.push(`{\\pard\\ql\\sa120\\cf1\\fs22 ${rtfEscape(s.skills.join('  -  '))}\\par}`);
    }
  }

  if (data.experience && data.experience.length) {
    sectionHeader('Experience');
    data.experience.forEach((exp) => {
      const titleLine = [
        exp.title ? `{\\b ${rtfEscape(exp.title)}\\b0}` : '',
        exp.company ? ` \\u8212? ${rtfEscape(exp.company)}` : '',
        exp.location ? `, ${rtfEscape(exp.location)}` : '',
      ].join('');
      const dateRange = [exp.start, exp.end].filter(Boolean).join(' \\u8212? ');
      out.push(`{\\pard\\ql\\sb120\\sa40\\tx9000\\tqr\\tx9000\\cf1\\fs22 ${titleLine}\\tab ${rtfEscape(dateRange)}\\par}`);
      if (exp.summary) {
        const italic = template.italicRoles ? '\\i' : '';
        const italicEnd = template.italicRoles ? '\\i0' : '';
        out.push(`{\\pard\\ql\\sa40\\cf1\\fs22 ${italic}${rtfEscape(exp.summary)}${italicEnd}\\par}`);
      }
      if (exp.bullets && exp.bullets.length) {
        exp.bullets.forEach((b) => {
          out.push(`{\\pard\\fi-280\\li360\\sa30\\cf1\\fs22 \\u8226? \\tab ${rtfEscape(b)}\\par}`);
        });
      }
      out.push(`{\\pard\\sb60\\par}`);
    });
  }

  if (data.education && data.education.length) {
    sectionHeader('Education');
    data.education.forEach((ed) => {
      const line = [
        ed.degree ? `{\\b ${rtfEscape(ed.degree)}\\b0}` : '',
        ed.school ? ` \\u8212? ${rtfEscape(ed.school)}` : '',
        ed.location ? `, ${rtfEscape(ed.location)}` : '',
      ].join('');
      out.push(`{\\pard\\ql\\sb80\\sa40\\tx9000\\tqr\\tx9000\\cf1\\fs22 ${line}\\tab ${rtfEscape(ed.date || '')}\\par}`);
      if (ed.honors) {
        out.push(`{\\pard\\ql\\sa40\\cf2\\fs21\\i ${rtfEscape(ed.honors)}\\i0\\par}`);
      }
    });
  }

  if (data.certifications && data.certifications.length) {
    sectionHeader('Certifications & Licenses');
    data.certifications.forEach((c) => {
      const parts = [
        c.name ? `{\\b ${rtfEscape(c.name)}\\b0}` : '',
        c.org ? `, ${rtfEscape(c.org)}` : '',
        c.date ? ` (${rtfEscape(c.date)})` : '',
      ].join('');
      out.push(`{\\pard\\fi-280\\li360\\sa40\\cf1\\fs22 \\u8226? \\tab ${parts}\\par}`);
    });
  }

  if (data.projects && data.projects.length) {
    sectionHeader('Projects');
    data.projects.forEach((p) => {
      if (p.name) {
        out.push(`{\\pard\\ql\\sb80\\sa40\\cf1\\fs22\\b ${rtfEscape(p.name)}\\b0\\par}`);
      }
      if (p.description) {
        out.push(`{\\pard\\ql\\sa40\\cf1\\fs22 ${rtfEscape(p.description)}\\par}`);
      }
      if (p.bullets && p.bullets.length) {
        p.bullets.forEach((b) => {
          out.push(`{\\pard\\fi-280\\li360\\sa30\\cf1\\fs22 \\u8226? \\tab ${rtfEscape(b)}\\par}`);
        });
      }
    });
  }

  if (data.awards && data.awards.length) {
    sectionHeader('Awards & Honors');
    data.awards.forEach((a) => {
      out.push(`{\\pard\\fi-280\\li360\\sa40\\cf1\\fs22 \\u8226? \\tab ${rtfEscape(a)}\\par}`);
    });
  }

  out.push(`}`);

  const rtf = out.join('\n');
  const blob = new Blob([rtf], { type: 'application/rtf' });
  downloadBlob(blob, filename);
}
